import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import type { PrototypeInspectDto, PrototypeInspectEnvVar } from '@aiai/shared';
import { fetchPrototypeInspect } from '../api';
import { qk } from '../api/queryKeys';
import { Spinner } from './Spinner';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}

function KV({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex min-h-[1.5rem] items-start gap-2 py-0.5">
      <span className="w-32 flex-none text-xs text-ink-muted">{label}</span>
      <span className={`min-w-0 flex-1 break-all text-xs text-ink ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}

function EnvRow({ entry, reveal }: { entry: PrototypeInspectEnvVar; reveal: boolean }) {
  return (
    <div className="flex min-h-[1.5rem] items-start gap-1 py-0.5 font-mono text-xs">
      <span className="flex-none text-signal">{entry.key}</span>
      <span className="flex-none text-ink-muted">=</span>
      <span className={`min-w-0 flex-1 break-all ${entry.redacted ? 'text-ink-muted italic' : 'text-ink'}`}>
        {entry.redacted && !reveal ? '***' : entry.value}
      </span>
    </div>
  );
}

function InspectContent({ data }: { data: PrototypeInspectDto }) {
  const [revealEnv, setRevealEnv] = useState(false);

  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const hasSecrets = data.env.some((e) => e.redacted);

  return (
    <div className="space-y-5 p-4">
      <Section title="Container">
        <div className="rounded-lg border border-line bg-surface-2 p-3 space-y-0.5">
          <KV label="Container ID" value={data.containerId} mono />
          <KV label="Image" value={data.image} mono />
          <KV label="Platform" value={data.platform} mono />
          <KV label="Created" value={fmtDate(data.createdAt)} />
          <KV label="Started" value={fmtDate(data.startedAt)} />
          <KV label="Restart policy" value={data.restartPolicy} mono />
        </div>
      </Section>

      <Section title="Resources">
        <div className="rounded-lg border border-line bg-surface-2 p-3 space-y-0.5">
          <KV label="CPU limit" value={data.cpuLimit > 0 ? `${data.cpuLimit} vCPU` : 'none'} />
          <KV label="Memory limit" value={data.memLimitMb > 0 ? `${data.memLimitMb} MiB` : 'none'} />
          <KV label="PID limit" value={data.pidsLimit >= 0 ? String(data.pidsLimit) : 'none'} />
          <KV label="Read-only fs" value={data.readonlyRootfs ? 'yes' : 'no'} />
          {data.capDrop.length > 0 && (
            <KV label="Cap drop" value={data.capDrop.join(', ')} mono />
          )}
          {data.securityOpt.length > 0 && (
            <KV label="Security opts" value={data.securityOpt.join(', ')} mono />
          )}
        </div>
      </Section>

      {data.networks.length > 0 && (
        <Section title="Networks">
          <div className="rounded-lg border border-line bg-surface-2 p-3 space-y-0.5">
            {data.networks.map((n) => (
              <KV key={n.name} label={n.name} value={n.ip || '—'} mono />
            ))}
          </div>
        </Section>
      )}

      {data.ports.length > 0 && (
        <Section title="Ports">
          <div className="rounded-lg border border-line bg-surface-2 p-3">
            <span className="font-mono text-xs text-ink">{data.ports.join(', ')}</span>
          </div>
        </Section>
      )}

      {data.mounts.length > 0 && (
        <Section title="Mounts">
          <div className="rounded-lg border border-line bg-surface-2 p-3 divide-y divide-line">
            {data.mounts.map((m, i) => (
              <div key={i} className="space-y-0.5 py-2 first:pt-0 last:pb-0">
                <KV label="Source" value={m.source || '(tmpfs)'} mono />
                <KV label="Destination" value={m.destination} mono />
                <KV label="Access" value={m.readonly ? 'read-only' : 'read-write'} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {data.env.length > 0 && (
        <Section title="Environment">
          <div className="rounded-lg border border-line bg-surface-2 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              {hasSecrets && (
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-ink-muted hover:text-ink"
                  onClick={() => setRevealEnv((v) => !v)}
                >
                  {revealEnv ? (
                    <EyeOff className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <Eye className="h-3 w-3" aria-hidden="true" />
                  )}
                  {revealEnv ? 'Hide secrets' : 'Reveal secrets'}
                </button>
              )}
            </div>
            <div className="space-y-0.5">
              {data.env.map((e) => (
                <EnvRow key={e.key} entry={e} reveal={revealEnv} />
              ))}
            </div>
          </div>
        </Section>
      )}

      {Object.keys(data.labels).length > 0 && (
        <Section title="Labels">
          <div className="rounded-lg border border-line bg-surface-2 p-3 space-y-0.5">
            {Object.entries(data.labels).map(([k, v]) => (
              <KV key={k} label={k} value={v} mono />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/**
 * Portainer-style read-only container details panel. Fetches on demand (not
 * on page load) so it adds zero latency to the normal prototype view. Shown
 * when the prototype is live or stopped; the stats bar owns the toggle.
 */
export function PrototypeInspectPanel({
  prototypeId,
}: {
  prototypeId: string;
}) {
  const query = useQuery({
    queryKey: qk.prototypeInspect(prototypeId),
    queryFn: () => fetchPrototypeInspect(prototypeId),
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-line bg-surface-2 py-10">
        <Spinner className="h-5 w-5 text-ink-muted" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-xl border border-line bg-surface-2 px-4 py-6 text-center text-sm text-ink-muted">
        Could not load container details.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-surface-2">
      <InspectContent data={query.data!} />
    </div>
  );
}
